import {
  TextField,
  Grid,
  MenuItem,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";

export default function AbgrenzungForm() {
  const { control, setValue } = useFormContext();
  const [customDoc, setCustomDoc] = useState("");

  const varianten = [
    "nur im Unternehmen",
    "teilweise durch Steuerkanzlei",
    "vollständig durch Dienstleister",
  ];

  const dokumentenOptionen = [
    "AGB Steuerkanzlei",
    "Datenschutzvereinbarung",
    "Auftragsverarbeitungsvertrag (AVV)",
    "Service Level Agreement (SLA)",
    "Geheimhaltungsvereinbarung (NDA)",
    "IT-Sicherheitskonzept",
    "Vertrag zur Aufbewahrungspflicht",
    "Dokumentation der Verfahrensbeschreibung",
  ];

  const defaultSelected = [
    "AGB Steuerkanzlei",
    "Datenschutzvereinbarung",
    "Auftragsverarbeitungsvertrag (AVV)",
  ];

  // Beobachten der abgrenzungVariante + belegartenInfo
  const abgrenzungVariante = useWatch({
    control,
    name: "abgrenzungVariante",
  });

  const belegartenInfo = useWatch({
    control,
    name: "belegartenInfo",
  }) || "";

  const belegartenItems = Array.isArray(belegartenInfo)
    ? belegartenInfo.filter((item) => item && item.trim() !== "")
    : belegartenInfo
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const imUnternehmenBelegarten = useWatch({
    control,
    name: "ImUnternehmenBelegarten",
  }) || "";

  const selectedBelegarten = imUnternehmenBelegarten
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const dokumentenInfo = useWatch({
    control,
    name: "dokumentenverweise",
  }) || "";

  const currentItems = dokumentenInfo
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const handleToggleDokument = (item) => {
    const alreadyAdded = currentItems.includes(item);

    let updated;
    if (!alreadyAdded) {
      updated =
        currentItems.length === 0
          ? item
          : `${currentItems.join(", ")}, ${item}`;
    } else {
      updated = currentItems.filter((i) => i !== item).join(", ");
    }
    setValue("dokumentenverweise", updated.trim());
  };

  const handleToggleBelegart = (item) => {
    const alreadyAdded = selectedBelegarten.includes(item);

    let updated;
    if (!alreadyAdded) {
      updated =
        selectedBelegarten.length === 0
          ? item
          : `${selectedBelegarten.join(", ")}, ${item}`;
    } else {
      updated = selectedBelegarten.filter((i) => i !== item).join(", ");
    }
    setValue("ImUnternehmenBelegarten", updated.trim());
  };

  const handleAddCustomDoc = () => {
    const trimmed = customDoc.trim();
    if (trimmed === "" || currentItems.includes(trimmed)) return;

    const updated =
      currentItems.length === 0
        ? trimmed
        : `${currentItems.join(", ")}, ${trimmed}`;
    setValue("dokumentenverweise", updated);
    setCustomDoc("");
  };

  const isSelected = (item, inList) => inList.includes(item);

  useEffect(() => {
    if (currentItems.length === 0) {
      setValue("dokumentenverweise", defaultSelected.join(", "));
    }
  }, [currentItems.length, setValue]);

  return (
    <Grid container spacing={3} direction="column">
      <Grid item xs={12}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
      Einbindung externer Dienstleister in die Belegablage
              </Typography>
        <Typography variant="body1" gutterBottom>
          Auf dieser Seite dokumentieren Sie, ob die Belegablage und/oder Archivierung vollständig im Unternehmen erfolgt
          oder ob externe Dienstleister (z. B. eine Steuerberatungskanzlei) beteiligt sind.
          <br /><br />
          <strong>Wichtig:</strong> Wenn externe Dienstleister eingebunden sind, muss die Verfahrensdokumentation mit dem
          Partner abgestimmt werden. Ebenso sollten relevante Vereinbarungen (z. B. AGB, Service Level Agreements,
          Datenschutzvereinbarungen) dokumentiert und archiviert werden.
      
        </Typography>
      </Grid>
      

      {/* Bearbeitungsbereich */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
        Wählen Sie aus, ob die gesamte Belegablage intern erfolgt („nur im Unternehmen“),
        teilweise von einer Steuerkanzlei übernommen wird, oder ob die Ablage vollständig durch einen externen Dienstleister erfolgt.        </Typography>
        <Controller
          name="abgrenzungVariante"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Bearbeitungsbereich" select fullWidth>
              {varianten.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      {/* Kanzleiname */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Kanzleiname (falls zutreffend)
        </Typography>
        <Controller
          name="kanzleiName"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Kanzleiname (falls zutreffend)" fullWidth />
          )}
        />
      </Grid>

      {/* Verweise auf externe Dokumente */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
        Relevante Vereinbarungen mit Dienstleister auswählen.
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {dokumentenOptionen.map((item, index) => (
            <Grid item key={index}>
              <Chip
                label={item}
                clickable
                color={isSelected(item, currentItems) ? "primary" : "default"}
                variant={isSelected(item, currentItems) ? "filled" : "outlined"}
                onClick={() => handleToggleDokument(item)}
              />
            </Grid>
          ))}
        </Grid>

        {/* Eigenes Dokument hinzufügen */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={9}>
            <TextField
              label="Eigenes Dokument"
              fullWidth
              value={customDoc}
              onChange={(e) => setCustomDoc(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Button variant="outlined" fullWidth onClick={handleAddCustomDoc}>
              Hinzufügen
            </Button>
          </Grid>
        </Grid>

        <Controller
          name="dokumentenverweise"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Verweise auf externe Dokumente"
              multiline
              rows={3}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
          )}
        />
      </Grid>
{/* IMMER registrieren, auch wenn nicht sichtbar */}
<Controller
  name="ImUnternehmenBelegarten"
  control={control}
  defaultValue=""
  render={({ field }) => (
    <input type="hidden" {...field} />
  )}
 />

      {/* Dynamischer Picker für Belegarten bei "teilweise durch Steuerkanzlei" */}
      {abgrenzungVariante === "teilweise durch Steuerkanzlei" && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Bitte wählen Sie aus, welche Belegarten innerhalb des Unternehmens verarbeitet werden.
            Alle nicht ausgewählten Belegarten werden automatisch Ihrem Steuerberater zugeordnet.
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {belegartenItems.map((item, index) => (
              <Grid item key={index}>
                <Chip
                  label={item}
                  clickable
                  color={isSelected(item, selectedBelegarten) ? "primary" : "default"}
                  variant={isSelected(item, selectedBelegarten) ? "filled" : "outlined"}
                  onClick={() => handleToggleBelegart(item)}
                />
              </Grid>
            ))}
          </Grid>
          <Controller
            name="ImUnternehmenBelegarten"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label=""
                multiline
                rows={3}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
}
